import { useCallback, useEffect, useMemo, useState } from "react";
import type { LivePageData, LivePlaylistItem, LiveStreamRef, StreamSource } from "@tih/shared";
import { fetchLive } from "../api/client";
import { LiveComments } from "../components/LiveComments";
import { LiveReplayPlaylist } from "../components/LiveReplayPlaylist";
import { StreamPlayer } from "../components/StreamPlayer";
import { isBroadcastLive, resolveShowingStream } from "../lib/live-display";
import { useLiveStats } from "../hooks/useLiveStats";
import { useLiveSocket } from "../hooks/useLiveSocket";
import { useSiteCopy } from "../context/SiteCopyContext";
import { getAtPath } from "../lib/siteCopyPaths";
import { statsWatchLabel } from "../lib/statsDisplay";

/** Honest empty until /api/live responds — never invent a YouTube id. */
const EMPTY: LivePageData = {
  info: {
    isLive: false,
    current: null,
    last: {
      title: "Together In Heaven · Worship Overnight",
      subtitle: null,
      streamedAt: null,
    },
  },
  playlist: [],
};

function watchUrl(ref: LiveStreamRef): string | null {
  if (ref.youtubeId) return `https://www.youtube.com/watch?v=${encodeURIComponent(ref.youtubeId)}`;
  if (ref.source && (ref.source.kind === "hls" || ref.source.kind === "mp4")) return ref.source.src;
  if (ref.source?.kind === "youtube") return `https://www.youtube.com/watch?v=${encodeURIComponent(ref.source.src)}`;
  return null;
}

function resolveSource(ref: LiveStreamRef): StreamSource | null {
  if (ref.source) return ref.source;
  if (ref.youtubeId) return { kind: "youtube", src: ref.youtubeId };
  return null;
}

function playlistItemToRef(item: LivePlaylistItem): LiveStreamRef {
  return {
    source: item.source,
    youtubeId: item.youtubeId,
    title: item.title,
    subtitle: item.subtitle,
  };
}

function defaultReplayEventId(data: LivePageData): number | null {
  if (!data.info.last.streamedAt) return data.playlist[0]?.eventId ?? null;
  return (
    data.playlist.find((item) => item.streamedAt === data.info.last.streamedAt)?.eventId ??
    data.playlist[0]?.eventId ??
    null
  );
}

export function LivePage() {
  const [data, setData] = useState<LivePageData>(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [followBroadcast, setFollowBroadcast] = useState(true);
  const { copy } = useSiteCopy();
  const stats = useLiveStats();

  useEffect(() => {
    fetchLive()
      .then((next) => {
        setData(next);
        setSelectedEventId(defaultReplayEventId(next));
        setFollowBroadcast(true);
      })
      .catch(() => {
        /* keep empty — do not invent a stream */
      })
      .finally(() => setLoaded(true));
  }, []);

  const onLivePush = useCallback((info: LivePageData["info"]) => {
    setData((prev) => ({ ...prev, info }));
    if (info.isLive) {
      setFollowBroadcast(true);
    }
  }, []);
  useLiveSocket(onLivePush);

  const live = isBroadcastLive(data.info);
  const showing = useMemo((): LiveStreamRef | null => {
    if (followBroadcast || live) {
      return resolveShowingStream(data.info);
    }
    const picked = data.playlist.find((item) => item.eventId === selectedEventId);
    return picked ? playlistItemToRef(picked) : resolveShowingStream(data.info);
  }, [data, followBroadcast, live, selectedEventId]);

  const activePlaylistId = useMemo(() => {
    if (live && followBroadcast) return null;
    if (!followBroadcast && selectedEventId != null) return selectedEventId;
    return defaultReplayEventId(data);
  }, [data, followBroadcast, live, selectedEventId]);

  const source = showing ? resolveSource(showing) : null;
  const externalUrl = showing ? watchUrl(showing) : null;
  const watching = stats?.watchingNow.toLocaleString();
  const watchLabel = statsWatchLabel(stats, "watching now");
  const playlistTitle = String(getAtPath(copy, "live.playlistTitle") ?? "Recent overnights");
  const offlineNote = String(
    getAtPath(copy, "live.offlineNote") ??
      "The overnight is not broadcasting right now. When it goes live, this page will play it here.",
  );

  function onPlaylistSelect(item: LivePlaylistItem) {
    setSelectedEventId(item.eventId);
    setFollowBroadcast(false);
  }

  return (
    <div className="live-page">
      <div className="live-stage-grid">
        <div className="live-stage-main">
          <div className="live-player-frame">
            {source && showing ? (
              <StreamPlayer
                key={`${source.kind}:${source.src}`}
                source={source}
                live={live && followBroadcast}
                autoPlay={live && followBroadcast}
                title={showing.title}
              />
            ) : (
              <div className="live-empty">
                <div className="live-empty-inner">
                  <p className="live-empty-title">
                    {!loaded ? "Loading stream…" : "No stream is configured yet"}
                  </p>
                  {loaded && (
                    <p className="live-empty-body">
                      When the worship overnight goes live, it will play here. Check back closer to
                      the start time, or open the event page for the date and directions.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="live-meta">
            <div className="live-meta-top">
              <h1 className="live-title">{showing?.title ?? "Live stream"}</h1>
              {live && followBroadcast ? (
                <span className="live-badge live-badge-on">
                  <span className="lb-dot"></span>LIVE
                </span>
              ) : source ? (
                <span className="live-badge live-badge-off">REPLAY</span>
              ) : (
                <span className="live-badge live-badge-off">OFFLINE</span>
              )}
            </div>
            {showing?.subtitle && <p className="live-subtitle">{showing.subtitle}</p>}

            <div className="live-stats-row">
              {watching && (
                <div>
                  <b>{watching}</b>
                  <span>{watchLabel}</span>
                </div>
              )}
              {live && followBroadcast ? null : (
                <div className="live-offline-note">
                  {live && !followBroadcast
                    ? "Watching a past overnight — switch back to live below when you're ready."
                    : !live
                      ? offlineNote
                      : null}
                </div>
              )}
              {live && !followBroadcast && (
                <div className="live-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setFollowBroadcast(true)}
                  >
                    Back to live
                  </button>
                </div>
              )}
              {externalUrl && (
                <div className="live-actions">
                  <a
                    className="btn btn-ghost"
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {source?.kind === "youtube" ? "Open on YouTube" : "Open stream"}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        <LiveReplayPlaylist
          title={playlistTitle}
          items={data.playlist}
          activeEventId={activePlaylistId}
          onSelect={onPlaylistSelect}
        />
      </div>

      <LiveComments />
    </div>
  );
}
